/**
 * ============================================================================
 * NewDeviationDialog — modal for reporting a new deviation (Phase 7).
 * Owner: Phase 7 — Deviation Management Module.
 *
 * Posts to the PG-backed POST /api/v1/deviations (drafts/61 F0-8), which inserts
 * a row into deviation.deviations at status REPORTED with a DEV-YYYY-NNNNN number
 * and an SLA due date derived from the classification (CRITICAL 14d / MAJOR 30d /
 * MINOR 45d). On success the triage queue refetches so the new row appears.
 *
 * URS rows: DEV-001 (report), DEV-004 (classification).
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { buildHeaders, apiUrl } from '../../api/client';
import styles from './DeviationTriage.module.css';
import type { DeviationClassification, SourceArea } from './useDeviationsQuery';

export interface NewDeviationDialogProps {
  onClose: () => void;
  onCreated: (deviationId: string) => void;
}

interface FormState {
  title: string;
  description: string;
  classification: DeviationClassification;
  sourceArea: SourceArea;
}

const INITIAL: FormState = {
  title: '',
  description: '',
  classification: 'MAJOR',
  sourceArea: 'MANUFACTURING',
};

const CLASSIFICATIONS: DeviationClassification[] = ['CRITICAL', 'MAJOR', 'MINOR'];
const SOURCE_AREAS: SourceArea[] = [
  'MANUFACTURING',
  'PACKAGING',
  'WAREHOUSE',
  'LAB',
  'UTILITIES',
  'QA',
  'IT',
  'OTHER',
];

// SLA window per classification — mirrors the backend (drafts/61 F0-8) so the
// reporter sees the commitment they are creating before they submit.
const SLA_DAYS: Record<DeviationClassification, number> = {
  CRITICAL: 14,
  MAJOR: 30,
  MINOR: 45,
};

export function NewDeviationDialog({ onClose, onCreated }: NewDeviationDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  function update<K extends keyof FormState>(key: K, v: FormState[K]) {
    setForm((p) => ({ ...p, [key]: v }));
  }

  async function submit() {
    if (!form.title.trim() || !form.description.trim()) {
      setErrorMsg(
        t('deviation.create.missingRequired', {
          defaultValue: 'A title and a description of what happened are required.',
        }),
      );
      return;
    }
    setSubmitting(true);
    setErrorMsg(undefined);
    try {
      const r = await fetch(apiUrl('/api/v1/deviations'), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({
          title: form.title.trim(),
          description: form.description.trim(),
          classification: form.classification,
          sourceArea: form.sourceArea,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMsg(
          (body as { detail?: string }).detail ??
            t('deviation.create.failed', { defaultValue: 'Report deviation failed.' }),
        );
        return;
      }
      const body = (await r.json()) as { id?: string; deviationId?: string };
      onCreated(body.deviationId ?? body.id ?? '');
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.dialogShade} role="dialog" aria-modal="true">
      <div className={styles.dialogBox}>
        <h2 className={styles.dialogTitle}>
          {t('deviation.create.title', { defaultValue: 'Report a deviation' })}
        </h2>
        <p className={styles.dialogSubtitle}>
          {t('deviation.create.subtitle', {
            defaultValue:
              'Opens a new record at REPORTED for QA triage. The SLA clock starts now.',
          })}
        </p>

        <div className={styles.formField}>
          <label htmlFor="dev-title">
            {t('deviation.create.titleLabel', { defaultValue: 'Title' })}
          </label>
          <input
            id="dev-title"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder={t('deviation.create.titlePlaceholder', {
              defaultValue: 'Short summary, e.g. "Tablet hardness out of range — Line 3"',
            })}
          />
        </div>

        <div className={styles.formField}>
          <label htmlFor="dev-desc">
            {t('deviation.create.descLabel', { defaultValue: 'What happened?' })}
          </label>
          <textarea
            id="dev-desc"
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder={t('deviation.create.descPlaceholder', {
              defaultValue:
                'Describe the event, when it was discovered, the affected batch/equipment, and any immediate action taken.',
            })}
          />
        </div>

        <div className={styles.twoCol}>
          <div className={styles.formField}>
            <label htmlFor="dev-class">
              {t('deviation.create.classLabel', { defaultValue: 'Classification' })}
            </label>
            <select
              id="dev-class"
              value={form.classification}
              onChange={(e) => update('classification', e.target.value as DeviationClassification)}
            >
              {CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>
                  {t(`deviation.classification.${c}`, { defaultValue: c })}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formField}>
            <label htmlFor="dev-area">
              {t('deviation.create.areaLabel', { defaultValue: 'Source area' })}
            </label>
            <select
              id="dev-area"
              value={form.sourceArea}
              onChange={(e) => update('sourceArea', e.target.value as SourceArea)}
            >
              {SOURCE_AREAS.map((a) => (
                <option key={a} value={a}>
                  {t(`deviation.sourceArea.${a}`, { defaultValue: a })}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className={styles.dialogSubtitle} aria-live="polite">
          {t('deviation.create.slaHint', {
            defaultValue: 'Investigation SLA for a {{level}} deviation: {{days}} days.',
            level: t(`deviation.classification.${form.classification}`, {
              defaultValue: form.classification,
            }),
            days: SLA_DAYS[form.classification],
          })}
        </p>

        {errorMsg && (
          <div className={styles.errorBanner} role="alert">
            {errorMsg}
          </div>
        )}

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting
              ? t('common.submitting', { defaultValue: 'Submitting…' })
              : t('deviation.create.submit', { defaultValue: 'Report deviation' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
